import { useState, useRef, useEffect, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X, Search, Plus, TestTube } from "lucide-react";
import { cn } from "@/lib/utils";

// Comprehensive investigation options - organized by category
const INVESTIGATION_OPTIONS = [
  // Blood Tests - Most Common
  "CBC", "CBC with ESR", "ESR", "Hemoglobin", "Platelet Count", "PCV", "Total Count", "Differential Count",
  
  // Sugar Tests
  "FBS", "PPBS", "RBSL", "HbA1c", "Random Blood Sugar", "FASTING and POSTMEAL BSL", "Glucose Tolerance Test",
  
  // Liver Function Tests
  "LFT", "SGPT/ALT", "SGOT/AST", "Bilirubin Total", "Bilirubin Direct", "Alkaline Phosphatase", "Total Protein", "Albumin", "GGT",
  
  // Kidney Function Tests
  "KFT", "CREATININE", "Urea", "BUN", "Uric Acid", "Microalbumin", "24hr Urine Protein",
  
  // Lipid Profile
  "Lipid Profile", "Total Cholesterol", "HDL Cholesterol", "LDL Cholesterol", "Triglycerides", "VLDL",
  
  // Cardiac Tests
  "ECG", "2D Echo", "Stress Test", "Troponin I", "Troponin T", "CK-MB", "Pro BNP", "D-Dimer",
  
  // Thyroid Tests
  "Thyroid Profile", "TSH", "T3", "T4", "Free T3", "Free T4", "Anti-TPO", "Thyroglobulin",
  
  // Inflammatory Markers
  "CRP", "C-Reactive Protein", "RA Factor", "Anti CCP", "ANA", "Anti dsDNA",
  
  // Coagulation Tests
  "PT INR", "PTT", "APTT", "Bleeding Time", "Clotting Time", "Fibrinogen",
  
  // Imaging - X-Ray
  "Chest X-Ray", "CXR PA VIEW", "X-Ray Abdomen", "X-Ray Spine", "X-Ray Joint",
  
  // Imaging - Ultrasound
  "USG ABDO PELVIS", "Ultrasound Abdomen", "USG Pelvis", "USG Neck", "USG Thyroid", "USG Heart",
  
  // Imaging - Advanced
  "CT BRAIN P", "CT Scan Abdomen", "CT Chest", "MRI Brain", "MRI Spine", "Mammography",
  
  // Urine Tests
  "Urine Routine", "Urine R/M", "Urine Culture", "Urine Microscopy", "24hr Urine", "Urine Protein",
  
  // Microbiology & Infectious Disease
  "Blood Culture", "Sputum Culture", "Stool Culture", "Throat Swab", "Wound Culture",
  "HBsAg", "Anti HCV", "HIV", "VDRL", "TPHA", "Widal Test", "Typhidot",
  "Malaria Parasite", "Malaria Antigen", "Dengue NS1", "Dengue IgM/IgG", "Chikungunya IgM",
  
  // Electrolytes & Minerals
  "SODIUM / POTASSIUM", "Serum Sodium", "Serum Potassium", "Serum Chloride", "Calcium", "Phosphorus", "Magnesium",
  
  // Vitamins & Nutrients
  "Vitamin D", "Vitamin D3", "Vitamin B12", "Folate", "Iron Studies", "Ferritin", "TIBC",
  
  // Hormonal Tests
  "Cortisol", "Testosterone", "Prolactin", "LH", "FSH", "Estradiol", "Progesterone",
  
  // Tumor Markers
  "PSA", "CEA", "CA 19-9", "CA 125", "AFP", "Beta HCG",
  
  // Special Tests
  "H.PYLORI TESTING", "Helicobacter Pylori", "Mantoux Test", "Sputum AFB", "Sputum Gene Xpert",
  
  // Pulmonary Function
  "PFT", "Pulmonary Function Test", "Peak Flow", "Chest CT"
];

export default function InvestigationsGrid() {
  const { setValue, watch, formState: { errors } } = useFormContext();
  const [searchInput, setSearchInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const selectedTests = watch("investigations") || [];

  // Helper to get investigation name
  const getInvestigationName = (item: any) =>
    typeof item === 'string' ? item : (item && typeof item.investigation === 'string' ? item.investigation : '');

  // Smart filtering with prioritized ordering
  const filteredSuggestions = (() => {
    if (!searchInput.trim()) return [];
    
    const searchLower = searchInput.toLowerCase().trim();
    const matches = INVESTIGATION_OPTIONS.filter(test => !selectedTests.includes(test));
    
    // Categorize matches by priority
    const exactMatches = [];
    const startsWithMatches = [];
    const wordStartsMatches = [];
    const containsMatches = [];
    
    matches.forEach(test => {
      const testLower = test.toLowerCase();
      
      if (testLower === searchLower) {
        exactMatches.push(test);
      } else if (testLower.startsWith(searchLower)) {
        startsWithMatches.push(test);
      } else if (test.split(' ').some(word => word.toLowerCase().startsWith(searchLower)) ||
                 test.split('/').some(part => part.toLowerCase().trim().startsWith(searchLower))) {
        wordStartsMatches.push(test);
      } else if (testLower.includes(searchLower)) {
        containsMatches.push(test);
      }
    });
    
    // Return prioritized results
    return [
      ...exactMatches,
      ...startsWithMatches,
      ...wordStartsMatches,
      ...containsMatches
    ].slice(0, 8);
  })();

  // Auto-select first item when search changes
  useEffect(() => {
    if (showSuggestions && filteredSuggestions.length > 0) {
      setFocusedIndex(0);
    } else {
      setFocusedIndex(-1);
    }
  }, [searchInput, showSuggestions, filteredSuggestions.length]);

  // Handle adding investigation
  const addInvestigation = useCallback((test: string) => {
    const trimmedTest = test.trim();
    if (trimmedTest && !selectedTests.includes(trimmedTest)) {
      setValue("investigations", [...selectedTests, trimmedTest], { shouldDirty: true });
    }
    setSearchInput("");
    setShowSuggestions(false);
    setFocusedIndex(-1);
    inputRef.current?.focus();
  }, [selectedTests, setValue]);

  // Handle removing investigation
  const removeInvestigation = useCallback((test: string) => {
    setValue("investigations", selectedTests.filter((t: string) => t !== test), { shouldDirty: true });
  }, [selectedTests, setValue]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const maxIndex = filteredSuggestions.length - 1;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex(prev => prev < maxIndex ? prev + 1 : 0);
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : maxIndex);
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredSuggestions.length) {
          addInvestigation(filteredSuggestions[focusedIndex]);
        } else if (searchInput.trim()) {
          addInvestigation(searchInput.trim());
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setFocusedIndex(-1);
        break;
      case "Backspace":
        if (searchInput === "" && selectedTests.length > 0) {
          e.preventDefault();
          removeInvestigation(selectedTests[selectedTests.length - 1]);
        }
        break;
      case "Tab":
        setShowSuggestions(false);
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="space-y-4">


      {/* Main Input Container */}
      <div className="relative" ref={containerRef}>
        <div
          className={cn(
                      "min-h-[42px] p-3 border border-border rounded-lg cursor-text transition-all bg-card",
          "hover:border-ring/60",
            showSuggestions && "ring-2 ring-[hsl(var(--medical-investigations)/0.2)] border-[hsl(var(--medical-investigations))]"
          )}
          onClick={() => {
            inputRef.current?.focus();
            setShowSuggestions(true);
          }}
        >
          {/* Selected Investigation Tags */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {selectedTests.map((test: any, index: number) => {
              const name = getInvestigationName(test);
              if (!name) return null;
              return (
                <Badge
                  key={`${name}-${index}`}
                  variant="secondary"
                  className="h-6 px-2 text-xs bg-[hsl(var(--medical-investigations)/0.1)] text-[hsl(var(--medical-investigations))] border border-[hsl(var(--medical-investigations)/0.3)] hover:bg-[hsl(var(--medical-investigations)/0.2)] transition-colors"
                >
                  <span className="font-medium">{name}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeInvestigation(name);
                    }}
                    className="ml-1.5 hover:text-destructive transition-colors"
                    title="Remove test"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              );
            })}
          </div>

          {/* Search Input */}
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <Input
              id="investigation-search"
              name="investigation-search"
              ref={inputRef}
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => {
                setShowSuggestions(true);
                if (filteredSuggestions.length > 0) {
                  setFocusedIndex(0);
                }
              }}
              onKeyDown={handleKeyDown}
              placeholder={selectedTests.length === 0 ? "Search tests (CBC, FBS, ECG, X-Ray...)" : "Add more tests..."}
                              className="border-0 bg-transparent h-7 px-0 focus-visible:ring-0 text-sm placeholder:text-muted-foreground text-foreground"
            />
          </div>

          {/* Quick Add Buttons */}
          {selectedTests.length === 0 && !searchInput && !showSuggestions && (
                          <div className="mt-2 pt-2 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Quick add:</div>
              <div className="flex flex-wrap gap-1">
                {["CBC", "FBS", "ECG", "CXR PA VIEW", "Urine Routine"].map((test) => (
                  <button
                    key={test}
                    type="button"
                    onClick={() => addInvestigation(test)}
                                          className="px-2 py-1 text-xs bg-muted hover:bg-accent hover:text-accent-foreground border border-border hover:border-ring/50 rounded transition-colors"
                  >
                    + {test}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Suggestions Dropdown */}
        {showSuggestions && (searchInput || filteredSuggestions.length > 0) && (
                      <div className="absolute top-full mt-1 w-full bg-popover border border-border rounded-lg shadow-lg z-50 max-h-64 overflow-hidden">
            <div className="max-h-64 overflow-y-auto">
              {/* Filtered suggestions */}
              {filteredSuggestions.map((test, index) => (
                <button
                  key={test}
                  type="button"
                  onClick={() => addInvestigation(test)}
                  className={cn(
                    "w-full text-left px-3 py-2.5 text-sm hover:bg-[hsl(var(--medical-investigations)/0.1)] transition-colors",
                    "flex items-center justify-between group border-b border-border last:border-0",
                    index === focusedIndex && "bg-[hsl(var(--medical-investigations)/0.1)] text-[hsl(var(--medical-investigations))]"
                  )}
                >
                  <span className="font-medium">{test}</span>
                                      <Plus className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
              
              {/* Add custom test option */}
              {searchInput.trim() && !filteredSuggestions.some(test => 
                test.toLowerCase() === searchInput.toLowerCase()
              ) && (
                <button
                  type="button"
                  onClick={() => addInvestigation(searchInput.trim())}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors border-t border-border flex items-center gap-2"
                >
                  <Plus className="w-3 h-3 text-[hsl(var(--success))]" />
                  <span>Add custom: <strong className="text-[hsl(var(--success))]">"{searchInput.trim()}"</strong></span>
                </button>
              )}

              {/* No results state */}
              {filteredSuggestions.length === 0 && searchInput && (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                  <div className="mb-2">No matching tests found</div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addInvestigation(searchInput.trim())}
                    className="text-xs h-7"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add "{searchInput.trim()}"
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Summary */}
      {selectedTests.length > 0 && (
        <div className="text-xs text-muted-foreground bg-muted px-3 py-2 rounded border border-border">
          <strong>{selectedTests.length}</strong> investigation{selectedTests.length !== 1 ? 's' : ''} selected
        </div>
      )}

      {/* Form validation error */}
      {errors.investigations && (
        <div className="text-sm text-destructive">
          {typeof errors.investigations.message === 'string' 
            ? errors.investigations.message 
            : "Please check your investigations"}
        </div>
      )}
    </div>
  );
}